import { useCurrentUser } from "@qualio/ui-components";
import { useCreateProductStore } from "../useCreateProductStore";
import { useMutation } from "react-query";
import { designControlsApi } from "../../api/designControls";
import { useNavigate } from "react-router";
import { useCallback } from "react";
import { ProductWithoutId } from "@design-controls/types";
import { useCreateResourceLibraryProduct } from "../resourceLibrary/useCreateResourceLibraryProduct";

export const useCreateProduct = () => {
  const { companyId } = useCurrentUser();
  const navigate = useNavigate();
  const { product, requirements, testCases, risk, setProduct } =
    useCreateProductStore((state) => ({
      product: state.product,
      requirements: state.requirementPolicies,
      testCases: state.testCasePolicies,
      risk: state.riskPolicy,
      setProduct: state.setProduct,
    }));

  const createProductMutation = useMutation({
    mutationFn: (product: ProductWithoutId) =>
      designControlsApi.createProduct(companyId, product),
  });

  // create policies
  const createPolicies = useMutation({
    mutationFn: (productId: string) =>
      Promise.all(
        [...Object.values(requirements), ...Object.values(testCases), risk]
          .filter(Boolean)
          .map((policy) =>
            designControlsApi.createPolicy(companyId, productId, policy),
          ),
      ),
  });

  const createResourceLibraryProduct = useCreateResourceLibraryProduct();

  const createProduct = useCallback(async () => {
    let productPayload = product;
    if (product.productSource === "new") {
      const { matrixQRI } = await createResourceLibraryProduct.mutateAsync(
        product.resourceLibraryProductToCreate,
      );
      productPayload = {
        ...product,
        productSource: "existing",
        productQri: matrixQRI,
      };
      // Write the product back to the store, so that if we fail to create the DC Project,
      // we can retry with the now-created RL Product. Deliberately not
      // `storeProduct`: that is the product step's submit action and would
      // advance the wizard past its last step, blanking the body.
      setProduct(productPayload);
    }
    const createdProduct = await createProductMutation.mutateAsync({
      // Parse to trim any extra fields that won't be cared about.
      ...ProductWithoutId.parse(productPayload),
      // `none` omits `productQri` entirely, so parsing drops the key. Send an
      // explicit null so create and update agree on the wire shape.
      productQri:
        productPayload.productSource === "existing"
          ? productPayload.productQri
          : null,
    });
    createPolicies.mutate(createdProduct.id, {
      onSuccess: () => navigate(`/product/${createdProduct.id}`),
    });
  }, [
    createProductMutation,
    createPolicies,
    navigate,
    product,
    createResourceLibraryProduct,
    setProduct,
  ]);

  return {
    isLoading:
      createProductMutation.isLoading ||
      createPolicies.isLoading ||
      createResourceLibraryProduct.isLoading,
    createProduct,
  };
};
